import React from "react";
import {
  ScanLine,
  Braces,
  Table2,
  Workflow,
  CheckCircle2,
  ClipboardPlus,
  Eye,
  Brain,
  Sparkles,
  X,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";
import { FineTuningCallout } from "@/components/FineTuningCallout";

const CANONICAL = "https://www.hewto.ai/cms-1450-ocr";

const features = [
  { icon: ScanLine, title: "CMS-1450 OCR data capture", desc: "High-accuracy OCR data capture from UB-04 / CMS-1450 forms — scans, faxes, and multi-page institutional claims of any quality." },
  { icon: Table2, title: "CMS-1450 line item extraction", desc: "Line item extraction that splits revenue codes, HCPCS, units, and charges across every service line, even when claims span multiple pages." },
  { icon: Braces, title: "Charge description extraction", desc: "CMS-1450 charge description extraction pairs each revenue code with its description and amount, alongside condition, occurrence, value, and span codes." },
  { icon: Workflow, title: "837I-ready delivery", desc: "Outputs clean, validated data mapped for institutional EDI, ready for your clearinghouse or adjudication engine." },
];

const fields = [
  "FL 42–47: revenue codes, HCPCS, units & charges",
  "FL 18–28: condition codes",
  "FL 31–34: occurrence codes & dates",
  "FL 39–41: value codes & amounts",
  "FL 50–65: payer, insured & authorization detail",
  "FL 66–81: diagnosis, procedure & provider codes",
];

const faqs = [
  {
    q: "What is CMS-1450 line item extraction?",
    a: "CMS-1450 line item extraction is the automated capture of each service line on a UB-04 / CMS-1450 claim — revenue code, HCPCS/CPT, units, and charge amount. Hewto.ai extracts every line item, including claims that span multiple pages, and returns structured, validated data.",
  },
  {
    q: "How does your CMS-1450 OCR data capture work?",
    a: "Our CMS-1450 OCR data capture reads UB-04 forms from scans, faxes, and PDFs using layout-aware AI, maps each form locator (FL) to a structured field with confidence scoring, validates the values, and delivers 837I-ready output via API, webhook, or SFTP.",
  },
  {
    q: "Can Hewto.ai handle CMS-1450 charge description extraction?",
    a: "Yes. CMS-1450 charge description extraction pairs each revenue code (FL 42) with its charge description (FL 43), units (FL 46), and total charges (FL 47), so you get complete, itemized line-level detail for every institutional claim.",
  },
  {
    q: "Is Hewto.ai a UB-04 OCR software?",
    a: "Hewto.ai is intelligent UB-04 OCR software purpose-built for US healthcare. It automates institutional claim intake end-to-end — OCR data capture, line item extraction, charge description extraction, validation, and EDI delivery — for payers, providers, clearinghouses, and RCM teams.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const CMS1450 = () => (
  <PageTransition>
    <Seo
      title="UB-04 OCR & Paper Claim Conversion to 837I | Hewto.ai"
      description="Hewto.ai UB-04 OCR software automates institutional paper claims processing to EDI 837I — CMS-1450 line item extraction, revenue-code capture, and charge description extraction. Convert scanned UB-04 / CMS-1450 forms into clean, 837I-ready data with automated paper claim conversion, EDI medical claims generation, CMS claims processing, and electronic medical claims delivery for hospitals and payers."
      keywords="paper claim conversion, paper claims processing, paper claims processing software, paper claims conversion, paper claim conversion software, paper claim conversion service, paper claim conversion api, edi medical claims, edi medical claims software, edi medical claims 837, edi medical claims processing, cms claims processing, cms claims processing software, cms 1450 claims processing, electronic medical claims, electronic medical claims processing, electronic medical claims submission, ub-04 paper claim conversion, cms 1450 paper claim conversion, institutional paper claim conversion, paper to edi 837i conversion, automated paper claim conversion ub-04, ub-04 ocr software, cms-1450 ocr, cms-1450 line item extraction, cms-1450 ocr data capture, cms 1450 charge description extraction, ub-04 data capture, ub-04 to 837i converter, convert ub-04 to edi 837 institutional, ub-04 revenue code extraction, agentic document extraction ub-04"
      canonical={CANONICAL}
      image="https://www.hewto.ai/ub04-form.png"
      jsonLd={faqJsonLd}
    />
    <PageHero
      eyebrow="Products · UB-04 OCR Software"
      title="UB-04 OCR software for CMS-1450 line item extraction"
      subtitle="Hewto.ai automates CMS-1450 paper claim conversion — line item extraction, revenue-code capture, and charge description extraction — turning complex UB-04 institutional forms into clean, validated, 837I-ready data. End-to-end paper claim conversion for hospitals, payers, and clearinghouses."
    />

    {/* DEMO VIDEO */}
    <section id="demo" className="scroll-mt-20 bg-slate-50 py-24">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              See it in action
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Watch Hewto.ai process a UB-04 in real time
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              A short walkthrough of ingesting, extracting, and validating a UB-04 / CMS-1450
              institutional claim — from scan to 837I-ready structured data.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            data-testid="demo-video"
            className="relative mt-12 overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 shadow-2xl shadow-brand/10"
          >
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src="https://www.youtube.com/embed/aNJopP_WRWc?rel=0&modestbranding=1"
                title="Hewto.ai UB-04 / CMS-1450 product demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="demo-video-iframe"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {features.map((f, i) => (
          <Reveal key={f.title} delay={(i % 4) * 0.08}>
            <div className="group h-full rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand/10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <f.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* WHY WE BEAT TRADITIONAL OCR — Agentic VLM */}
    <section className="relative overflow-hidden bg-slate-950 py-24 text-slate-100">
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(4, 135, 216, 0.35) 0, transparent 45%), radial-gradient(circle at 85% 80%, rgba(4, 135, 216, 0.25) 0, transparent 50%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/30 bg-brand-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-200">
              <Sparkles className="h-3.5 w-3.5" /> Agentic Document Extraction
            </span>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Why Hewto.ai outperforms traditional UB-04 OCR
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-300">
              We don&apos;t use character-by-character OCR. Hewto.ai runs an agentic document
              extraction pipeline built on{" "}
              <span className="text-white">
                vision-language models fine-tuned specifically for the UB-04 / CMS-1450
                institutional claim form
              </span>
              . The model reads the claim the way a trained hospital biller does — looking at the
              whole form, its 81 form locators, and every visual cue — so it captures details a
              traditional OCR engine silently misses.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Eye className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Vision-first, not character-first</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                A VLM sees the UB-04 as a whole — 81 boxes, revenue-code grids, occurrence and
                condition codes, signatures, stamps, and long value-code stacks — instead of
                stitching guesses letter by letter. It reads checkbox crosses and ticks the way a
                human eye does, and tells watermark text apart from value text, label text from
                value text, and background from foreground. Visual context stays intact.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Brain className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Fine-tuned on UB-04 / CMS-1450</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Our models are fine-tuned on real UB-04 institutional claims across payers, scan
                qualities, and facility layouts. They know FL 42 is a revenue-code line — not just
                a grid of characters — so extractions map cleanly to fields, not raw text.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Workflow className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Agentic reasoning, per field</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Each field runs through a purpose-built agent that verifies context, cross-checks
                related form locators, and self-corrects when something looks off — the kind of
                judgment a deterministic OCR pipeline simply can&apos;t make.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-1 gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-2 md:grid-cols-2 md:gap-0">
            <div className="p-8">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-red-300/80">
                <X className="h-4 w-4" /> Traditional OCR misses
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                {[
                  "Revenue-code lines misaligned across FL 42 / 43 / 44 / 47",
                  "Occurrence codes, value codes, and condition codes stacked in FL 18–41",
                  "Checkbox / tick selections for admission type and priority",
                  "Watermarks and background print bleeding into extracted values",
                  "Label text confused with the value text it describes",
                  "Handwritten patient signatures and dates of service",
                  "Long HCPCS/CPT tables with modifiers on institutional lines",
                  "Facility stamps, initials, and hand-corrections",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-brand-500/15 to-brand-500/5 p-8 md:m-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-200">
                <CheckCircle2 className="h-4 w-4" /> Hewto.ai captures
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-100">
                {[
                  "Every revenue-code line correctly aligned with charges and units",
                  "Occurrence, value, and condition codes parsed into their FL slots",
                  "All checkboxes and ticks — including partially marked ones",
                  "Crosses, ticks, and checkmarks read the way a human eye reads them",
                  "Watermark text separated from real value text — background noise ignored",
                  "Label text distinguished from the value text it describes",
                  "Handwriting in any box, including signatures and hand-written dates",
                  "HCPCS/CPT + modifier stacks preserved per service line",
                  "Stamps, initials, and hand-corrections preserved with confidence scores",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-200" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <FineTuningCallout formName="UB-04 / CMS-1450 forms" />

    <section className="bg-slate-50 py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg">
              <img
                src="/ub04-form.png"
                alt="UB-04 / CMS-1450 institutional claim form"
                className="w-full object-cover"
              />
            </div>
            <div className="absolute bottom-4 right-4 w-52 rounded-2xl border border-slate-100 bg-white p-4 shadow-xl">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-50 text-brand">
                  <ClipboardPlus className="h-3.5 w-3.5" />
                </span>
                UB-04 · extracted
              </div>
              <div className="mt-3 space-y-2 font-mono text-xs">
                {[
                  ["Rev code", "0450"],
                  ["HCPCS", "99284"],
                  ["Value (A2)", "$1,240.00"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-slate-400">{k}</span>
                    <span className="font-semibold text-slate-900">{v}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 pt-1 text-emerald-600">
                  <CheckCircle2 className="h-3.5 w-3.5" /> 99.1% confidence
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Form coverage
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Every form locator, captured
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              From revenue codes and charge descriptions to value and occurrence spans, Hewto.ai&apos;s
              UB-04 OCR software captures every form locator — powering accurate CMS-1450 line item
              extraction and complete charge description extraction on every institutional claim.
            </p>
          </Reveal>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.map((f, i) => (
              <Reveal key={f} delay={(i % 2) * 0.06}>
                <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  <span className="text-sm text-slate-700">{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
      <Reveal>
        <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
          UB-04 OCR software FAQ
        </span>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          CMS-1450 line item &amp; charge description extraction
        </h2>
        <p className="mt-4 leading-relaxed text-slate-600">
          Common questions about CMS-1450 OCR data capture, line item extraction, and charge
          description extraction with Hewto.ai&apos;s UB-04 OCR software.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-8" data-testid="cms1450-faq">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger
                data-testid={`cms1450-faq-trigger-${i}`}
                className="text-left text-base font-semibold text-slate-900 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>

    <CTABand />
  </PageTransition>
);

export default CMS1450;
